import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { PrintTemplateView, ReportView } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { useSubResource } from '#client/hooks/useResourceItem';
import { type PrintJobOptions, printUrls } from '#client/lib/endpoints';
import { RESOURCE, routes } from '#client/lib/routes';
import { PdfFrame } from './PdfFrame';
import { PrintOptions } from './PrintOptions';
import { countOf } from '#client/lib/format';
import { useAllRecords } from '#client/hooks/useAllRecords';

/**
 * The print screen.
 *
 * The PDF is a URL the server serves, so this component holds no document — only
 * the query string that describes one. Changing an option changes the `src`, the
 * server renders again, and Download and Open are ordinary links to the same
 * address. Nothing is built in the browser, so there is no blob URL to leak
 * and the file can be saved like any other download.
 */
export function PrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const reports = useSubResource<ReportView[]>(RESOURCE.patients, id, 'reports');
  // Templates are a handful of rows and the picker shows them all; there is no
  // paging affordance on this screen and none is wanted mid-print.
  const templates = useAllRecords<PrintTemplateView>({
    path: RESOURCE.printTemplates,
  });

  const [options, setOptions] = useState<PrintJobOptions>({});

  // `?report=` pre-selects one test — the report-entry screen's Print button
  // means "print this one". Read once, into the initial state, because it is a
  // starting point rather than a source of truth: from here on the tick boxes
  // own the selection.
  const [params] = useSearchParams();
  const requested = Number(params.get('report'));
  /** `undefined` means every report — see PrintOptions. */
  const [selected, setSelected] = useState<number[] | undefined>(
    Number.isInteger(requested) && requested > 0 ? [requested] : undefined,
  );

  const active = useMemo(
    () =>
      templates.items.find((template) => template.id === options.template_id) ??
      templates.items.find((template) => template.is_default) ??
      templates.items[0],
    [templates.items, options.template_id],
  );

  const job: PrintJobOptions = { ...options, report_ids: selected };
  const src = id === undefined ? undefined : printUrls.patient(id, job);
  const downloadUrl =
    id === undefined ? undefined : printUrls.patient(id, { ...job, download: true });

  const back = (
    <Button
      icon={<ArrowLeft />}
      aria-label="Back"
      onClick={() => void navigate(routes.patient(id ?? ''))}
    />
  );

  if (reports.loading) return <PageLoading title="Print" backAction={back} rows={3} />;

  if (reports.error) {
    return (
      <PageMessage title="Print" backAction={back}>
        {reports.error}
      </PageMessage>
    );
  }

  const all = reports.data ?? [];
  if (all.length === 0) {
    return (
      <PageMessage title="Print" backAction={back} tone="info">
        This patient has no reports to print.
      </PageMessage>
    );
  }

  const count = (selected ?? all).length;

  return (
    <Page
      title="Print"
      subtitle={`${count} of ${countOf(all.length, 'test')}`}
      fullWidth
      backAction={back}
    >
      <div className="c-print">
        <PrintOptions
          templates={templates.items}
          active={active}
          reports={all}
          selected={selected}
          onSelectedChange={setSelected}
          options={options}
          onChange={(patch) => setOptions((current) => ({ ...current, ...patch }))}
        />

        <PdfFrame
          className="c-print__preview"
          src={count === 0 ? undefined : src}
          downloadUrl={downloadUrl}
          title="Printed reports"
          message={count === 0 ? 'Select at least one test to print.' : undefined}
        />
      </div>
    </Page>
  );
}
